import type { CreatorTagBatchPayload, CreatorTagColor } from "@unbox/api";
import { useEffect, useRef, useState } from "react";
import { useBatchSaveCreatorTags, useCreatorTags, useDownloadCreatorTagTemplate } from "@/api/creator-tags";
import { randomTagColor } from "@/lib/creator-tags/colors";
import { error, success } from "@/providers/ToastProvider";

// A staged row in the editor: either an existing server tag (with a server `id`)
// or a brand-new one (client-generated UUID). All edits live here until Save
// commits them as a single batch.
export type DraftRow = {
  id: string;
  name: string;
  description: string;
  color: CreatorTagColor;
  source: "manual" | "csv";
  userId?: string;
  /** Display name of the tag's owner (creating user); empty for unsaved rows. */
  ownerName: string;
  assignedCount: number;
};

/**
 * Shared editing state for the tag library, used by both the in-table modal
 * (`tag-library-modal.tsx`) and the Operations page (`index.tsx`). Owns the
 * draft, the diff against the server snapshot, save, and inline-delete state;
 * each container supplies its own chrome (footer / discard guard).
 *
 * `active` gates seeding: the modal passes its `open` flag (seed on open, reset
 * on close); the page passes `true` (always seeded).
 */
export const useTagLibraryEditor = ({ active }: { active: boolean }) => {
  const { data: serverTags } = useCreatorTags();
  const { mutateAsync: saveBatch, isPending } = useBatchSaveCreatorTags();
  const downloadTemplate = useDownloadCreatorTagTemplate();

  const [draft, setDraft] = useState<DraftRow[]>([]);
  const [search, setSearch] = useState("");
  // Row awaiting an inline delete confirmation (assigned tags only).
  const [confirmingDeleteId, setConfirmingDeleteId] = useState<string | null>(null);
  const [importOpen, setImportOpen] = useState(false);

  // Snapshot of the server state at seed time, used to compute the diff on Save.
  const original = useRef<Map<string, { name: string; description: string; color: string }>>(new Map());
  const seeded = useRef(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Seed the editor from the server list once per activation; reset on deactivation.
  useEffect(() => {
    if (active && serverTags && !seeded.current) {
      original.current = new Map(
        serverTags.map((t) => [t.id, { name: t.name, description: t.description ?? "", color: t.color }]),
      );
      setDraft(
        serverTags.map((t) => ({
          id: t.id,
          name: t.name,
          description: t.description ?? "",
          color: t.color as CreatorTagColor,
          source: t.source,
          userId: t.userId,
          ownerName: [t.ownerFirstName, t.ownerLastName].filter(Boolean).join(" "),
          assignedCount: t.assignedCount,
        })),
      );
      seeded.current = true;
    }
    if (!active) {
      seeded.current = false;
      setDraft([]);
      setSearch("");
      setConfirmingDeleteId(null);
    }
  }, [active, serverTags]);

  const handleDownloadTemplate = async () => {
    try {
      await downloadTemplate();
    } catch {
      error("Could not download the template");
    }
  };

  const updateRow = (id: string, patch: Partial<DraftRow>) =>
    setDraft((rows) => rows.map((row) => (row.id === id ? { ...row, ...patch } : row)));

  const addNewTag = () => {
    // Auto-assign a random palette colour.
    const color = randomTagColor();
    setDraft((rows) => [
      ...rows,
      { id: crypto.randomUUID(), name: "", description: "", color, source: "manual", ownerName: "", assignedCount: 0 },
    ]);
    // Scroll the new (empty) row into view and focus its name input once it renders.
    requestAnimationFrame(() => {
      const el = scrollContainerRef.current;
      if (!el) return;
      el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
      const inputs = el.querySelectorAll<HTMLInputElement>('input[aria-label="Tag name"]');
      inputs[inputs.length - 1]?.focus();
    });
  };

  const removeRow = (row: DraftRow) => {
    // Assigned tags need an explicit confirm (inline, not a stacked modal);
    // unassigned ones are low-stakes and drop straight out of the draft.
    if (row.assignedCount > 0) {
      setConfirmingDeleteId(row.id);
      return;
    }
    setDraft((rows) => rows.filter((r) => r.id !== row.id));
  };

  const confirmDelete = (row: DraftRow) => {
    setDraft((rows) => rows.filter((r) => r.id !== row.id));
    setConfirmingDeleteId(null);
  };

  const cancelDelete = () => setConfirmingDeleteId(null);

  // Diff the current draft against the seed snapshot to build the save payload.
  // Deletes fall out for free: any snapshot id no longer in the draft was removed.
  const computeBatch = (): CreatorTagBatchPayload => {
    const create: CreatorTagBatchPayload["create"] = [];
    const update: CreatorTagBatchPayload["update"] = [];
    const remove: string[] = [];
    const draftIds = new Set(draft.map((row) => row.id));

    for (const row of draft) {
      const name = row.name.trim();
      const before = original.current.get(row.id);
      if (!before) {
        create.push({ name, color: row.color });
        continue;
      }
      if (before.name !== name || before.color !== row.color) {
        update.push({ id: row.id, name, color: row.color });
      }
    }
    for (const id of original.current.keys()) {
      if (!draftIds.has(id)) remove.push(id);
    }

    return { create, update, delete: remove };
  };

  const isBatchDirty = (batch: CreatorTagBatchPayload) =>
    batch.create.length > 0 || batch.update.length > 0 || batch.delete.length > 0;

  const isDirty = isBatchDirty(computeBatch());

  /**
   * Validate + persist the batch. Returns whether the save (or no-op) succeeded
   * so the container can decide what to do next (the modal closes; the page
   * stays open). On success the snapshot is marked stale so it re-seeds from the
   * refreshed server list.
   */
  const handleSave = async (): Promise<boolean> => {
    if (draft.some((row) => row.name.trim() === "")) {
      error("Every tag needs a name");
      return false;
    }
    const batch = computeBatch();
    if (!isBatchDirty(batch)) return true;
    try {
      await saveBatch(batch);
      success("Tag library saved");
      seeded.current = false;
      return true;
    } catch {
      // Global handler surfaces the error toast; keep edits so the user can fix
      // the offending tag (e.g. a duplicate name).
      return false;
    }
  };

  const visibleRows = draft.filter(
    (row) => !row.id || search.trim() === "" || row.name.toLowerCase().includes(search.trim().toLowerCase()),
  );

  return {
    draft,
    visibleRows,
    search,
    setSearch,
    importOpen,
    setImportOpen,
    confirmingDeleteId,
    scrollContainerRef,
    isPending,
    isDirty,
    updateRow,
    addNewTag,
    removeRow,
    confirmDelete,
    cancelDelete,
    handleSave,
    handleDownloadTemplate,
  };
};

export type TagLibraryEditor = ReturnType<typeof useTagLibraryEditor>;
